import { MODEL_VERSION, resolveApiPath } from '@/app/constants';
import type { ModelVersion } from '@/app/types';

export const FINISHED_STATES = ['success', 'failed', 'canceled'];

export const THIRD_PARTY_AUDIT_ILLEGAL_ERROR_CODE = 'ThirdPartyAuditIllegal';

export const TASK_STATE_URL = resolveApiPath('/vidu/v1/tasks/state');

export const FIRST_CREATION_INDEX = 0;

// 时间常量
export const MINUTES_IN_HOUR = 60;
export const MINUTES_IN_DAY = 1440; // 24 * 60
export const ONE_MINUTE = 1;

/**
 * 显示额外提示所需的最小等待时长（单位：分钟）
 */
export const MIN_HINT_WAIT_MINUTES = 3;

// 默认兜底时间
export const DEFAULT_ESTIMATED_TIME = 50;
/** 默认生图生成时间 */
export const DEFAULT_IMG_ESTIMATED_TIME = 25;

/** 动作控制预估：13s 动作视频约 6 分钟，较长视频适当增加等待时间 */
export const MOTION_CONTROL_BASE_ESTIMATED_TIME = 204;
export const MOTION_CONTROL_ESTIMATED_TIME_PER_SECOND = 12;

// 模版预估生成时间
export const TEMPLATE_ESTIMATED_TIME = 15;

// 模型推理预估时间（不含 recaption）
export const ESTIMATED_TIME: Record<ModelVersion, Record<string, Record<string, Record<number, number>>>> = {
  [MODEL_VERSION['2.0']]: {
    character2video: {
      '512': {
        4: 10,
      },
      '720p': {
        4: 50,
        8: 70, // 未支持
      },
      '1080p': {
        4: 120, // 未支持
      },
    },
    default: {
      '512': {
        4: 6,
      },
      '720p': {
        4: 38,
        8: 28,
      },
      '1080p': {
        4: 32,
      },
    },
  },
  [MODEL_VERSION.Q1]: {
    character2video: {
      '1080p': {
        5: 420, //参考生7分钟
      },
    },
    default: {
      '1080p': {
        5: 300, //文生5分，图生3分暂时放一起了
      },
    },
  },
  [MODEL_VERSION.Q2]: {
    img2video: {
      speed: {
        2: 15,
        3: 20,
        4: 30,
        5: 35,
        6: 40,
        7: 50,
        8: 60,
      },
      pro: {
        2: 30,
        3: 40,
        4: 60,
        5: 80,
        6: 115,
        7: 125,
        8: 160,
      },
    },
    headtailimg2video: {
      speed: {
        2: 20,
        3: 25,
        4: 30,
        5: 35,
        6: 45,
        7: 55,
        8: 65,
      },
      pro: {
        2: 30,
        3: 45,
        4: 65,
        5: 85,
        6: 110,
        7: 135,
        8: 165,
      },
    },
    text2video: {
      default: {
        2: 18,
        3: 23,
        4: 27,
        5: 32,
        6: 36,
        7: 45,
        8: 53,
      },
    },
    character2video: {
      default: {
        2: 16,
        3: 20,
        4: 27,
        5: 35,
        6: 45,
        7: 55,
        8: 65,
      },
      with_audio: {
        2: 45,
        3: 50,
        4: 55,
        5: 60,
        6: 80,
        7: 100,
        8: 120,
      },
      lip_sync: {
        2: 155,
        3: 160,
        4: 170,
        5: 180,
        6: 200,
        7: 210,
        8: 220,
      },
    },
  },
  [MODEL_VERSION.Q2_PRO]: {
    character2video: {
      default: {
        2: 16,
        3: 20,
        4: 27,
        5: 35,
        6: 45,
        7: 55,
        8: 65,
      },
      with_audio: {
        2: 45,
        3: 50,
        4: 55,
        5: 60,
        6: 80,
        7: 100,
        8: 120,
      },
      lip_sync: {
        2: 155,
        3: 160,
        4: 170,
        5: 180,
        6: 200,
        7: 210,
        8: 220,
      },
    },
  },
  [MODEL_VERSION.Q3]: {
    character2video: {
      speed: {
        1: 40,
        2: 40,
        3: 40,
        4: 50,
        5: 60,
        6: 70,
        7: 80,
        8: 105,
        9: 130,
        10: 150,
        11: 165,
        12: 180,
        13: 200,
        14: 235,
        15: 260,
        16: 300,
      },
      pro: {
        1: 2074,
        2: 1283,
        3: 929,
        4: 1419,
        5: 1410,
        6: 1201,
        7: 1257,
        8: 968,
        9: 1165,
        10: 1063,
        11: 5496,
        12: 3970,
        13: 6336,
        14: 3909,
        15: 1818,
        16: 1634,
      },
      pro_ad: {
        3: 60,
        4: 85,
        5: 100,
        6: 140,
        7: 160,
        8: 210,
        9: 270,
        10: 300,
        11: 370,
        12: 390,
        13: 500,
        14: 550,
        15: 625,
        16: 765,
      },
    },
    img2video: {
      speed: {
        1: 25,
        2: 25,
        3: 25,
        4: 35,
        5: 45,
        6: 55,
        7: 65,
        8: 80,
        9: 90,
        10: 100,
        11: 120,
        12: 130,
        13: 150,
        14: 160,
        15: 170,
        16: 200,
      },
      pro: {
        1: 30,
        2: 40,
        3: 60,
        4: 85,
        5: 125,
        6: 150,
        7: 170,
        8: 240,
        9: 290,
        10: 340,
        11: 420,
        12: 470,
        13: 550,
        14: 610,
        15: 695,
        16: 785,
      },
    },
    headtailimg2video: {
      speed: {
        1: 25,
        2: 25,
        3: 25,
        4: 35,
        5: 45,
        6: 55,
        7: 65,
        8: 80,
        9: 90,
        10: 100,
        11: 120,
        12: 130,
        13: 150,
        14: 160,
        15: 170,
        16: 200,
      },
      pro: {
        1: 30,
        2: 40,
        3: 60,
        4: 85,
        5: 100,
        6: 140,
        7: 160,
        8: 210,
        9: 270,
        10: 300,
        11: 370,
        12: 390,
        13: 500,
        14: 550,
        15: 625,
        16: 765,
      },
    },
    text2video: {
      speed: {
        1: 25,
        2: 25,
        3: 25,
        4: 35,
        5: 45,
        6: 55,
        7: 65,
        8: 80,
        9: 90,
        10: 100,
        11: 120,
        12: 130,
        13: 140,
        14: 150,
        15: 170,
        16: 200,
      },
      pro: {
        1: 30,
        2: 40,
        3: 60,
        4: 85,
        5: 125,
        6: 150,
        7: 170,
        8: 220,
        9: 290,
        10: 320,
        11: 400,
        12: 450,
        13: 510,
        14: 570,
        15: 640,
        16: 775,
      },
    },
  },
  [MODEL_VERSION.Q4]: {
    img2video: {
      default: {
        3: 25,
        4: 35,
        5: 45,
        6: 55,
        7: 65,
        8: 80,
        9: 90,
        10: 100,
        11: 120,
        12: 130,
        13: 150,
        14: 160,
        15: 170,
        16: 200,
      },
    },
  },
  [MODEL_VERSION.Q3_PRO]: {},
  [MODEL_VERSION.Q3_FAST]: {},
  [MODEL_VERSION.Q3_LITE]: {},
  [MODEL_VERSION.GPT_IMAGE_2]: {},
  [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: {},
  [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: {},
  [MODEL_VERSION.Q3_A]: {
    character2video: {
      default: {
        4: 200,
        5: 200,
        6: 200,
        7: 200,
        8: 250,
        9: 300,
        10: 300,
        11: 350,
        12: 400,
        13: 450,
        14: 500,
        15: 500,
      },
    },
    img2video: {
      default: {
        4: 200,
        5: 200,
        6: 200,
        7: 200,
        8: 250,
        9: 300,
        10: 300,
        11: 350,
        12: 400,
        13: 450,
        14: 500,
        15: 500,
      },
    },
    text2video: {
      default: {
        4: 200,
        5: 200,
        6: 200,
        7: 200,
        8: 250,
        9: 300,
        10: 300,
        11: 350,
        12: 400,
        13: 450,
        14: 500,
        15: 500,
      },
    },
  },
  [MODEL_VERSION.Q3_HH]: {
    character2video: {
      default: {
        4: 200,
        5: 200,
        6: 200,
        7: 200,
        8: 250,
        9: 300,
        10: 300,
        11: 350,
        12: 400,
        13: 450,
        14: 500,
        15: 500,
      },
    },
    img2video: {
      default: {
        4: 200,
        5: 200,
        6: 200,
        7: 200,
        8: 250,
        9: 300,
        10: 300,
        11: 350,
        12: 400,
        13: 450,
        14: 500,
        15: 500,
      },
    },
    text2video: {
      default: {
        4: 200,
        5: 200,
        6: 200,
        7: 200,
        8: 250,
        9: 300,
        10: 300,
        11: 350,
        12: 400,
        13: 450,
        14: 500,
        15: 500,
      },
    },
  },
};

export const IMG_ESTIMATED_TIME: Partial<Record<ModelVersion, Record<string, Record<string, number>>>> = {
  [MODEL_VERSION.Q1]: {
    reference2image: {
      '1080p': 25,
    },
    default: {
      '1080p': 25,
    },
  },
  [MODEL_VERSION.Q2]: {
    reference2image: {
      '1080p': 25,
      '2k': 30,
      '4k': 35,
    },
    text2image: {
      '1080p': 25,
      '2k': 30,
      '4k': 35,
    },
    default: {
      '1080p': 25,
      '2k': 30,
      '4k': 35,
    },
  },
  [MODEL_VERSION.Q3_PRO]: {
    default: {
      '1080p': 60,
      '2k': 120,
      '4k': 180,
    },
  },
  [MODEL_VERSION.Q3_FAST]: {
    default: {
      '1080p': 60,
      '2k': 120,
      '4k': 180,
    },
  },
  [MODEL_VERSION.Q3_LITE]: {
    default: {
      '1080p': 60,
    },
  },
  [MODEL_VERSION.GPT_IMAGE_2]: {
    default: {
      '1080p': 60,
      '2k': 120,
      '4k': 180,
    },
  },
  [MODEL_VERSION.GPT_IMAGE_2_5_FAST]: {
    default: {
      '1080p': 60,
      '2k': 120,
      '4k': 180,
    },
  },
  [MODEL_VERSION.GPT_IMAGE_2_5_PRO]: {
    default: {
      '1080p': 60,
      '2k': 120,
      '4k': 180,
    },
  },
};
